/**
 * Enterprise-Grade HIPAA-Compliant Audit Log Model
 * 
 * This model supports comprehensive audit logging for healthcare environments
 * with full HIPAA compliance, PHI access tracking, and regulatory requirements.
 * 
 * HIPAA Requirements Addressed:
 * - 164.312(b) - Audit controls
 * - 164.308(a)(1)(ii)(D) - Information system activity review
 * - 164.312(c)(1) - Integrity controls
 * - 164.312(d) - Person or entity authentication
 */

// ============================================================================
// AUDIT LOG ACTIONS - Comprehensive action tracking
// ============================================================================

export enum AuditLogAction {
  // Authentication & Session
  userLogin = 'user_login',
  userLogout = 'user_logout',
  userLoginFailed = 'user_login_failed',
  sessionCreated = 'session_created',
  sessionExpired = 'session_expired',
  sessionTerminated = 'session_terminated',
  mfaEnabled = 'mfa_enabled',
  mfaDisabled = 'mfa_disabled',
  mfaVerified = 'mfa_verified',
  mfaFailed = 'mfa_failed',
  
  // User Account Management
  userCreated = 'user_created',
  userUpdated = 'user_updated',
  userDeleted = 'user_deleted',
  userStatusChanged = 'user_status_changed',
  userRoleChanged = 'user_role_changed',
  passwordChanged = 'password_changed',
  passwordReset = 'password_reset',
  passwordResetRequested = 'password_reset_requested',
  accountLocked = 'account_locked',
  accountUnlocked = 'account_unlocked',
  
  // Employer Management
  employerCreated = 'employer_created',
  employerUpdated = 'employer_updated',
  employerDeleted = 'employer_deleted',
  employerStatusChanged = 'employer_status_changed',
  employerAdminAdded = 'employer_admin_added',
  employerAdminRemoved = 'employer_admin_removed',
  
  // Member Management (PHI-related)
  memberCreated = 'member_created',
  memberUpdated = 'member_updated',
  memberDeleted = 'member_deleted',
  memberEnrolled = 'member_enrolled',
  memberTerminated = 'member_terminated',
  memberDependentAdded = 'member_dependent_added',
  memberDependentRemoved = 'member_dependent_removed',
  
  // Provider Actions
  providerCreated = 'provider_created',
  providerUpdated = 'provider_updated',
  providerDeleted = 'provider_deleted',
  providerSubscribed = 'provider_subscribed',
  providerVerified = 'provider_verified',
  
  // Receipt/Claims Actions (PHI-related)
  receiptSubmitted = 'receipt_submitted',
  receiptViewed = 'receipt_viewed',
  receiptApproved = 'receipt_approved',
  receiptRejected = 'receipt_rejected',
  receiptPaid = 'receipt_paid',
  receiptModified = 'receipt_modified',
  receiptDeleted = 'receipt_deleted',
  
  // Financial Actions
  paymentProcessed = 'payment_processed',
  paymentFailed = 'payment_failed',
  reimbursementIssued = 'reimbursement_issued',
  expenseCreated = 'expense_created',
  expenseApproved = 'expense_approved',
  expenseRejected = 'expense_rejected',
  invoiceCreated = 'invoice_created',
  invoiceSent = 'invoice_sent',
  invoicePaid = 'invoice_paid',
  
  // Benefit Plan Actions
  benefitPlanCreated = 'benefit_plan_created',
  benefitPlanUpdated = 'benefit_plan_updated',
  benefitPlanDeleted = 'benefit_plan_deleted',
  benefitPlanEnrollment = 'benefit_plan_enrollment',
  
  // PHI Access (HIPAA Critical)
  phiAccessed = 'phi_accessed',
  phiViewed = 'phi_viewed',
  phiModified = 'phi_modified',
  phiDeleted = 'phi_deleted',
  phiExported = 'phi_exported',
  phiPrinted = 'phi_printed',
  phiEmailed = 'phi_emailed',
  phiDisclosed = 'phi_disclosed',
  
  // Data Operations
  dataViewed = 'data_viewed',
  dataExported = 'data_exported',
  dataImported = 'data_imported',
  dataDeleted = 'data_deleted',
  dataBulkOperation = 'data_bulk_operation',
  dataBackupCreated = 'data_backup_created',
  dataRestored = 'data_restored',
  
  // Security Events
  unauthorizedAccess = 'unauthorized_access',
  permissionDenied = 'permission_denied',
  suspiciousActivity = 'suspicious_activity',
  securityAlertTriggered = 'security_alert_triggered',
  ipBlocked = 'ip_blocked',
  brute_force_detected = 'brute_force_detected',
  
  // Admin/System Actions
  settingsChanged = 'settings_changed',
  featureToggled = 'feature_toggled',
  bulkOperation = 'bulk_operation',
  systemConfigChanged = 'system_config_changed',
  apiKeyCreated = 'api_key_created',
  apiKeyRevoked = 'api_key_revoked',
  
  // Compliance Actions
  policyAcknowledged = 'policy_acknowledged',
  consentGiven = 'consent_given',
  consentRevoked = 'consent_revoked',
  baaExecuted = 'baa_executed',
  complianceCheckPerformed = 'compliance_check_performed',
  
  // Report Actions
  reportGenerated = 'report_generated',
  reportViewed = 'report_viewed',
  reportExported = 'report_exported',
  auditReportGenerated = 'audit_report_generated',

  // Team Workspace Actions
  meetingCreated = 'meeting_created',
  meetingUpdated = 'meeting_updated',
  meetingDeleted = 'meeting_deleted',
  meetingProcessed = 'meeting_processed',
  meetingAccessChanged = 'meeting_access_changed',
  decisionCreated = 'decision_created',
  decisionUpdated = 'decision_updated',
  decisionDeleted = 'decision_deleted',
  decisionApproved = 'decision_approved',
  decisionRejected = 'decision_rejected',
  decisionImplemented = 'decision_implemented',
  taskCreated = 'task_created',
  taskUpdated = 'task_updated',
  taskDeleted = 'task_deleted',
  taskAssigned = 'task_assigned',
  taskStarted = 'task_started',
  taskCompleted = 'task_completed',
  taskBlocked = 'task_blocked',
  taskCancelled = 'task_cancelled',
  riskCreated = 'risk_created',
  riskUpdated = 'risk_updated',
  riskDeleted = 'risk_deleted',
  riskAssessed = 'risk_assessed',
  riskMitigated = 'risk_mitigated',
  riskRealized = 'risk_realized',
  questionCreated = 'question_created',
  questionUpdated = 'question_updated',
  questionDeleted = 'question_deleted',
  questionAnswered = 'question_answered',
  questionDeferred = 'question_deferred',
  phiDetected = 'phi_detected',
  phiFlagCleared = 'phi_flag_cleared',
  approvalStepCompleted = 'approval_step_completed',

  // Other
  other = 'other',
}

// ============================================================================
// AUDIT LOG SEVERITY
// ============================================================================

export enum AuditLogSeverity {
  info = 'info',
  warning = 'warning',
  error = 'error',
  critical = 'critical',
}

// ============================================================================
// PHI DATA TYPES (for HIPAA tracking)
// ============================================================================

export enum PHIDataType {
  none = 'none',
  healthRecord = 'health_record',
  medicalHistory = 'medical_history',
  prescription = 'prescription',
  labResult = 'lab_result',
  diagnosis = 'diagnosis',
  treatmentPlan = 'treatment_plan',
  insuranceInfo = 'insurance_info',
  billingRecord = 'billing_record',
  demographicInfo = 'demographic_info',
  contactInfo = 'contact_info',
  ssn = 'ssn',
  financialInfo = 'financial_info',
  biometricData = 'biometric_data',
  geneticData = 'genetic_data',
  mentalHealth = 'mental_health',
  substanceAbuse = 'substance_abuse',
  other = 'other',
}

// ============================================================================
// ENTITY TYPES
// ============================================================================

export enum AuditEntityType {
  user = 'user',
  member = 'member',
  employer = 'employer',
  provider = 'provider',
  receipt = 'receipt',
  claim = 'claim',
  benefitPlan = 'benefit_plan',
  invoice = 'invoice',
  payment = 'payment',
  policy = 'policy',
  document = 'document',
  report = 'report',
  system = 'system',
  session = 'session',
  apiKey = 'api_key',
  teamMeeting = 'team_meeting',
  teamDecision = 'team_decision',
  teamTask = 'team_task',
  teamRisk = 'team_risk',
  teamQuestion = 'team_question',
  other = 'other',
}

// ============================================================================
// ACCESS REASON (HIPAA Break-the-Glass support)
// ============================================================================

export enum AccessReason {
  routine = 'routine',
  treatment = 'treatment',
  payment = 'payment',
  operations = 'operations',
  emergencyAccess = 'emergency_access',  // Break-the-Glass
  auditReview = 'audit_review',
  qualityAssurance = 'quality_assurance',
  research = 'research',
  legalRequest = 'legal_request',
  patientRequest = 'patient_request',
  publicHealth = 'public_health',
  lawEnforcement = 'law_enforcement',
  training = 'training',
  other = 'other',
}

// ============================================================================
// AUDIT LOG INTERFACE - HIPAA Compliant
// ============================================================================

export interface AuditLog {
  id: string;
  
  // Core Action Information
  action: AuditLogAction;
  severity: AuditLogSeverity;
  description: string;
  
  // Actor Information (who performed the action)
  userId?: string;
  userEmail?: string;
  userName?: string;
  userRole?: string;
  
  // Entity Information (what was affected)
  entityType?: AuditEntityType | string;
  entityId?: string;
  entityName?: string;
  
  // Session & Request Context
  sessionId?: string;
  requestId?: string;  // Correlation ID for distributed tracing
  
  // Network Information
  ipAddress?: string;
  userAgent?: string;
  geoLocation?: GeoLocation;
  
  // PHI/HIPAA Specific Fields
  containsPHI: boolean;
  phiDataTypes?: PHIDataType[];
  accessReason?: AccessReason;
  accessJustification?: string;  // Free text justification for emergency access
  patientId?: string;  // For patient-specific PHI access tracking
  
  // Change Tracking (Before/After)
  changeDetails?: ChangeDetails;
  
  // Result
  success: boolean;
  errorMessage?: string;
  errorCode?: string;
  
  // Additional Context
  details?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  tags?: string[];
  
  // Compliance Fields
  retentionPeriodDays?: number;  // HIPAA requires 6 years minimum
  isArchived?: boolean;
  archivedAt?: Date;
  
  // Integrity Fields
  hash?: string;  // SHA-256 hash for tamper detection
  previousLogHash?: string;  // Chain for integrity verification
  
  // Timestamps
  timestamp: Date;
  serverTimestamp?: Date;  // Server-side timestamp for accuracy
}

// ============================================================================
// SUPPORTING INTERFACES
// ============================================================================

export interface GeoLocation {
  country?: string;
  countryCode?: string;
  region?: string;
  city?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
}

export interface ChangeDetails {
  fieldsChanged?: string[];
  beforeValues?: Record<string, unknown>;
  afterValues?: Record<string, unknown>;
  changeType: 'create' | 'update' | 'delete' | 'access' | 'other';
}

// ============================================================================
// AUDIT LOG FILTER - Enhanced for HIPAA requirements
// ============================================================================

export interface AuditLogFilter {
  // Core Filters
  action?: AuditLogAction;
  actions?: AuditLogAction[];  // Multiple actions
  severity?: AuditLogSeverity;
  severities?: AuditLogSeverity[];
  
  // Actor Filters
  userId?: string;
  userEmail?: string;
  userRole?: string;
  
  // Entity Filters
  entityType?: AuditEntityType | string;
  entityId?: string;
  
  // PHI/HIPAA Filters
  containsPHI?: boolean;
  phiDataType?: PHIDataType;
  accessReason?: AccessReason;
  patientId?: string;
  
  // Network Filters
  ipAddress?: string;
  country?: string;
  
  // Date Filters
  startDate?: Date;
  endDate?: Date;
  
  // Result Filters
  success?: boolean;
  
  // Search
  searchQuery?: string;
  
  // Pagination
  limit?: number;
  offset?: number;
  lastDocumentId?: string;
}

// ============================================================================
// AUDIT LOG CREATION INPUT
// ============================================================================

export interface CreateAuditLogInput {
  action: AuditLogAction;
  severity?: AuditLogSeverity;
  description: string;
  
  // Actor (auto-populated if not provided)
  userId?: string;
  userEmail?: string;
  userName?: string;
  userRole?: string;
  
  // Entity
  entityType?: AuditEntityType | string;
  entityId?: string;
  entityName?: string;
  
  // Session
  sessionId?: string;
  requestId?: string;
  
  // Network (auto-populated if not provided)
  ipAddress?: string;
  userAgent?: string;
  
  // PHI Fields
  containsPHI?: boolean;
  phiDataTypes?: PHIDataType[];
  accessReason?: AccessReason;
  accessJustification?: string;
  patientId?: string;
  
  // Change Details
  changeDetails?: ChangeDetails;
  
  // Result
  success?: boolean;
  errorMessage?: string;
  errorCode?: string;
  
  // Additional
  details?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  tags?: string[];
}

// ============================================================================
// AUDIT STATISTICS & REPORTING
// ============================================================================

export interface AuditStatistics {
  totalLogs: number;
  logsByAction: Record<string, number>;
  logsBySeverity: Record<string, number>;
  logsByEntityType: Record<string, number>;
  phiAccessCount: number;
  failedActionsCount: number;
  uniqueUsers: number;
  uniqueIPs: number;
  
  // Time-based stats
  logsToday: number;
  logsThisWeek: number;
  logsThisMonth: number;
  
  // Security stats
  unauthorizedAccessAttempts: number;
  suspiciousActivities: number;
  emergencyAccessCount: number;
}

export interface AuditSummaryByUser {
  userId: string;
  userName: string;
  userEmail: string;
  totalActions: number;
  phiAccessCount: number;
  lastActivity: Date;
  actionBreakdown: Record<string, number>;
}

export interface PHIAccessReport {
  patientId?: string;
  patientName?: string;
  accessLogs: AuditLog[];
  accessCount: number;
  uniqueAccessors: number;
  accessByReason: Record<string, number>;
  dateRange: { start: Date; end: Date };
}

// ============================================================================
// HIPAA COMPLIANCE REPORT
// ============================================================================

export interface HIPAAComplianceReport {
  reportId: string;
  generatedAt: Date;
  generatedBy: string;
  dateRange: { start: Date; end: Date };
  
  // Access Statistics
  totalPHIAccesses: number;
  phiAccessByType: Record<PHIDataType, number>;
  phiAccessByReason: Record<AccessReason, number>;
  
  // User Activity
  userActivity: AuditSummaryByUser[];
  
  // Security Events
  securityIncidents: AuditLog[];
  unauthorizedAccessAttempts: AuditLog[];
  
  // Emergency Access (Break-the-Glass)
  emergencyAccessLogs: AuditLog[];
  emergencyAccessCount: number;
  
  // Integrity Verification
  logIntegrityStatus: 'verified' | 'tampered' | 'unknown';
  integrityCheckTimestamp: Date;
}

// ============================================================================
// ACTION LABELS FOR UI
// ============================================================================

export const ActionLabels: Record<AuditLogAction, string> = {
  // Authentication & Session
  [AuditLogAction.userLogin]: 'User Login',
  [AuditLogAction.userLogout]: 'User Logout',
  [AuditLogAction.userLoginFailed]: 'Login Failed',
  [AuditLogAction.sessionCreated]: 'Session Created',
  [AuditLogAction.sessionExpired]: 'Session Expired',
  [AuditLogAction.sessionTerminated]: 'Session Terminated',
  [AuditLogAction.mfaEnabled]: 'MFA Enabled',
  [AuditLogAction.mfaDisabled]: 'MFA Disabled',
  [AuditLogAction.mfaVerified]: 'MFA Verified',
  [AuditLogAction.mfaFailed]: 'MFA Failed',
  
  // User Account
  [AuditLogAction.userCreated]: 'User Created',
  [AuditLogAction.userUpdated]: 'User Updated',
  [AuditLogAction.userDeleted]: 'User Deleted',
  [AuditLogAction.userStatusChanged]: 'User Status Changed',
  [AuditLogAction.userRoleChanged]: 'User Role Changed',
  [AuditLogAction.passwordChanged]: 'Password Changed',
  [AuditLogAction.passwordReset]: 'Password Reset',
  [AuditLogAction.passwordResetRequested]: 'Password Reset Requested',
  [AuditLogAction.accountLocked]: 'Account Locked',
  [AuditLogAction.accountUnlocked]: 'Account Unlocked',
  
  // Employer
  [AuditLogAction.employerCreated]: 'Employer Created',
  [AuditLogAction.employerUpdated]: 'Employer Updated',
  [AuditLogAction.employerDeleted]: 'Employer Deleted',
  [AuditLogAction.employerStatusChanged]: 'Employer Status Changed',
  [AuditLogAction.employerAdminAdded]: 'Employer Admin Added',
  [AuditLogAction.employerAdminRemoved]: 'Employer Admin Removed',
  
  // Member
  [AuditLogAction.memberCreated]: 'Member Created',
  [AuditLogAction.memberUpdated]: 'Member Updated',
  [AuditLogAction.memberDeleted]: 'Member Deleted',
  [AuditLogAction.memberEnrolled]: 'Member Enrolled',
  [AuditLogAction.memberTerminated]: 'Member Terminated',
  [AuditLogAction.memberDependentAdded]: 'Dependent Added',
  [AuditLogAction.memberDependentRemoved]: 'Dependent Removed',
  
  // Provider
  [AuditLogAction.providerCreated]: 'Provider Created',
  [AuditLogAction.providerUpdated]: 'Provider Updated',
  [AuditLogAction.providerDeleted]: 'Provider Deleted',
  [AuditLogAction.providerSubscribed]: 'Provider Subscribed',
  [AuditLogAction.providerVerified]: 'Provider Verified',
  
  // Receipt/Claims
  [AuditLogAction.receiptSubmitted]: 'Receipt Submitted',
  [AuditLogAction.receiptViewed]: 'Receipt Viewed',
  [AuditLogAction.receiptApproved]: 'Receipt Approved',
  [AuditLogAction.receiptRejected]: 'Receipt Rejected',
  [AuditLogAction.receiptPaid]: 'Receipt Paid',
  [AuditLogAction.receiptModified]: 'Receipt Modified',
  [AuditLogAction.receiptDeleted]: 'Receipt Deleted',
  
  // Financial
  [AuditLogAction.paymentProcessed]: 'Payment Processed',
  [AuditLogAction.paymentFailed]: 'Payment Failed',
  [AuditLogAction.reimbursementIssued]: 'Reimbursement Issued',
  [AuditLogAction.expenseCreated]: 'Expense Created',
  [AuditLogAction.expenseApproved]: 'Expense Approved',
  [AuditLogAction.expenseRejected]: 'Expense Rejected',
  [AuditLogAction.invoiceCreated]: 'Invoice Created',
  [AuditLogAction.invoiceSent]: 'Invoice Sent',
  [AuditLogAction.invoicePaid]: 'Invoice Paid',
  
  // Benefit Plans
  [AuditLogAction.benefitPlanCreated]: 'Benefit Plan Created',
  [AuditLogAction.benefitPlanUpdated]: 'Benefit Plan Updated',
  [AuditLogAction.benefitPlanDeleted]: 'Benefit Plan Deleted',
  [AuditLogAction.benefitPlanEnrollment]: 'Benefit Plan Enrollment',
  
  // PHI Access
  [AuditLogAction.phiAccessed]: 'PHI Accessed',
  [AuditLogAction.phiViewed]: 'PHI Viewed',
  [AuditLogAction.phiModified]: 'PHI Modified',
  [AuditLogAction.phiDeleted]: 'PHI Deleted',
  [AuditLogAction.phiExported]: 'PHI Exported',
  [AuditLogAction.phiPrinted]: 'PHI Printed',
  [AuditLogAction.phiEmailed]: 'PHI Emailed',
  [AuditLogAction.phiDisclosed]: 'PHI Disclosed',
  
  // Data Operations
  [AuditLogAction.dataViewed]: 'Data Viewed',
  [AuditLogAction.dataExported]: 'Data Exported',
  [AuditLogAction.dataImported]: 'Data Imported',
  [AuditLogAction.dataDeleted]: 'Data Deleted',
  [AuditLogAction.dataBulkOperation]: 'Bulk Operation',
  [AuditLogAction.dataBackupCreated]: 'Backup Created',
  [AuditLogAction.dataRestored]: 'Data Restored',
  
  // Security
  [AuditLogAction.unauthorizedAccess]: 'Unauthorized Access',
  [AuditLogAction.permissionDenied]: 'Permission Denied',
  [AuditLogAction.suspiciousActivity]: 'Suspicious Activity',
  [AuditLogAction.securityAlertTriggered]: 'Security Alert',
  [AuditLogAction.ipBlocked]: 'IP Blocked',
  [AuditLogAction.brute_force_detected]: 'Brute Force Detected',
  
  // Admin/System
  [AuditLogAction.settingsChanged]: 'Settings Changed',
  [AuditLogAction.featureToggled]: 'Feature Toggled',
  [AuditLogAction.bulkOperation]: 'Bulk Operation',
  [AuditLogAction.systemConfigChanged]: 'System Config Changed',
  [AuditLogAction.apiKeyCreated]: 'API Key Created',
  [AuditLogAction.apiKeyRevoked]: 'API Key Revoked',
  
  // Compliance
  [AuditLogAction.policyAcknowledged]: 'Policy Acknowledged',
  [AuditLogAction.consentGiven]: 'Consent Given',
  [AuditLogAction.consentRevoked]: 'Consent Revoked',
  [AuditLogAction.baaExecuted]: 'BAA Executed',
  [AuditLogAction.complianceCheckPerformed]: 'Compliance Check',
  
  // Reports
  [AuditLogAction.reportGenerated]: 'Report Generated',
  [AuditLogAction.reportViewed]: 'Report Viewed',
  [AuditLogAction.reportExported]: 'Report Exported',
  [AuditLogAction.auditReportGenerated]: 'Audit Report Generated',

  // Team Workspace
  [AuditLogAction.meetingCreated]: 'Meeting Created',
  [AuditLogAction.meetingUpdated]: 'Meeting Updated',
  [AuditLogAction.meetingDeleted]: 'Meeting Deleted',
  [AuditLogAction.meetingProcessed]: 'Meeting Processed',
  [AuditLogAction.meetingAccessChanged]: 'Meeting Access Changed',
  [AuditLogAction.decisionCreated]: 'Decision Created',
  [AuditLogAction.decisionUpdated]: 'Decision Updated',
  [AuditLogAction.decisionDeleted]: 'Decision Deleted',
  [AuditLogAction.decisionApproved]: 'Decision Approved',
  [AuditLogAction.decisionRejected]: 'Decision Rejected',
  [AuditLogAction.decisionImplemented]: 'Decision Implemented',
  [AuditLogAction.taskCreated]: 'Task Created',
  [AuditLogAction.taskUpdated]: 'Task Updated',
  [AuditLogAction.taskDeleted]: 'Task Deleted',
  [AuditLogAction.taskAssigned]: 'Task Assigned',
  [AuditLogAction.taskStarted]: 'Task Started',
  [AuditLogAction.taskCompleted]: 'Task Completed',
  [AuditLogAction.taskBlocked]: 'Task Blocked',
  [AuditLogAction.taskCancelled]: 'Task Cancelled',
  [AuditLogAction.riskCreated]: 'Risk Created',
  [AuditLogAction.riskUpdated]: 'Risk Updated',
  [AuditLogAction.riskDeleted]: 'Risk Deleted',
  [AuditLogAction.riskAssessed]: 'Risk Assessed',
  [AuditLogAction.riskMitigated]: 'Risk Mitigated',
  [AuditLogAction.riskRealized]: 'Risk Realized',
  [AuditLogAction.questionCreated]: 'Question Created',
  [AuditLogAction.questionUpdated]: 'Question Updated',
  [AuditLogAction.questionDeleted]: 'Question Deleted',
  [AuditLogAction.questionAnswered]: 'Question Answered',
  [AuditLogAction.questionDeferred]: 'Question Deferred',
  [AuditLogAction.phiDetected]: 'PHI Detected',
  [AuditLogAction.phiFlagCleared]: 'PHI Flag Cleared',
  [AuditLogAction.approvalStepCompleted]: 'Approval Step Completed',

  [AuditLogAction.other]: 'Other',
};

export const SeverityLabels: Record<AuditLogSeverity, string> = {
  [AuditLogSeverity.info]: 'Info',
  [AuditLogSeverity.warning]: 'Warning',
  [AuditLogSeverity.error]: 'Error',
  [AuditLogSeverity.critical]: 'Critical',
};

export const PHIDataTypeLabels: Record<PHIDataType, string> = {
  [PHIDataType.none]: 'None',
  [PHIDataType.healthRecord]: 'Health Record',
  [PHIDataType.medicalHistory]: 'Medical History',
  [PHIDataType.prescription]: 'Prescription',
  [PHIDataType.labResult]: 'Lab Result',
  [PHIDataType.diagnosis]: 'Diagnosis',
  [PHIDataType.treatmentPlan]: 'Treatment Plan',
  [PHIDataType.insuranceInfo]: 'Insurance Information',
  [PHIDataType.billingRecord]: 'Billing Record',
  [PHIDataType.demographicInfo]: 'Demographic Information',
  [PHIDataType.contactInfo]: 'Contact Information',
  [PHIDataType.ssn]: 'Social Security Number',
  [PHIDataType.financialInfo]: 'Financial Information',
  [PHIDataType.biometricData]: 'Biometric Data',
  [PHIDataType.geneticData]: 'Genetic Data',
  [PHIDataType.mentalHealth]: 'Mental Health',
  [PHIDataType.substanceAbuse]: 'Substance Abuse',
  [PHIDataType.other]: 'Other',
};

export const AccessReasonLabels: Record<AccessReason, string> = {
  [AccessReason.routine]: 'Routine Access',
  [AccessReason.treatment]: 'Treatment',
  [AccessReason.payment]: 'Payment',
  [AccessReason.operations]: 'Healthcare Operations',
  [AccessReason.emergencyAccess]: 'Emergency Access',
  [AccessReason.auditReview]: 'Audit Review',
  [AccessReason.qualityAssurance]: 'Quality Assurance',
  [AccessReason.research]: 'Research',
  [AccessReason.legalRequest]: 'Legal Request',
  [AccessReason.patientRequest]: 'Patient Request',
  [AccessReason.publicHealth]: 'Public Health',
  [AccessReason.lawEnforcement]: 'Law Enforcement',
  [AccessReason.training]: 'Training',
  [AccessReason.other]: 'Other',
};

export const EntityTypeLabels: Record<AuditEntityType, string> = {
  [AuditEntityType.user]: 'User',
  [AuditEntityType.member]: 'Member',
  [AuditEntityType.employer]: 'Employer',
  [AuditEntityType.provider]: 'Provider',
  [AuditEntityType.receipt]: 'Receipt',
  [AuditEntityType.claim]: 'Claim',
  [AuditEntityType.benefitPlan]: 'Benefit Plan',
  [AuditEntityType.invoice]: 'Invoice',
  [AuditEntityType.payment]: 'Payment',
  [AuditEntityType.policy]: 'Policy',
  [AuditEntityType.document]: 'Document',
  [AuditEntityType.report]: 'Report',
  [AuditEntityType.system]: 'System',
  [AuditEntityType.session]: 'Session',
  [AuditEntityType.apiKey]: 'API Key',
  [AuditEntityType.teamMeeting]: 'Team Meeting',
  [AuditEntityType.teamDecision]: 'Team Decision',
  [AuditEntityType.teamTask]: 'Team Task',
  [AuditEntityType.teamRisk]: 'Team Risk',
  [AuditEntityType.teamQuestion]: 'Team Question',
  [AuditEntityType.other]: 'Other',
};
